<div class="space-y-2" aria-labelledby="file-upload-heading">
  <h3 id="file-upload-heading" class="sr-only">File upload</h3>
  <!-- Drop zone trigger -->
  <button
    type="button"
    (click)="fileUploadClick()"
    [disabled]="isDisabled() || isLoading()"
    [attr.aria-busy]="isLoading()"
    aria-describedby="file-upload-info file-upload-disabled-reason"
    class="group hover:bg-blue-75/40 w-full rounded-lg border border-dashed border-gray-200 bg-white px-3 py-3 text-left transition-colors hover:border-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 disabled:cursor-not-allowed disabled:opacity-50 lg:px-4"
  >
    <div class="flex items-start gap-2 lg:gap-3">
      <mat-icon
        class="mt-0.5 shrink-0 !text-xl transition-colors group-hover:text-blue-700 lg:!text-2xl"
        aria-hidden="true"
        >upload</mat-icon
      >
      <div class="min-w-0">
        <div
          class="font-lato text-sm font-bold text-gray-700 transition-colors group-hover:text-blue-600"
        >
          {{ isLoading() ? 'Uploading…' : 'Upload a file' }}
        </div>
        <p
          class="font-lato mt-0.5 text-xs leading-relaxed break-words text-gray-400"
        >
          <span class="block"
            ><strong>Allowed formats:</strong> PDF, Word (DOC, DOCX), Images
            (JPG, PNG, GIF, BMP).</span
          >
          <span class="block"
            ><strong>Maximum file size:</strong> 5&nbsp;MB.</span
          >
          <span class="block"
            >File names cannot contain special characters.</span
          >
        </p>
      </div>
    </div>
  </button>

  <!-- Hidden file input — not directly focusable; triggered by button above -->
  <input
    type="file"
    #fileUploads
    id="fileUploads"
    name="fileUploads"
    tabindex="-1"
    aria-hidden="true"
    class="hidden"
    (change)="onFileSelected($event)"
    [accept]="acceptedFileTypes"
  />

  <!-- Auto-upload note (referenced by aria-describedby) -->
  <p
    id="file-upload-info"
    class="font-lato flex items-start gap-1 text-xs leading-snug text-gray-400"
  >
    <mat-icon
      class="!flex !h-3.5 !w-3.5 !items-center !justify-center !text-sm"
      aria-hidden="true"
      >info</mat-icon
    >
    <span
      >Files upload immediately and a confirmation comment is posted
      automatically.</span
    >
  </p>

  <p id="file-upload-disabled-reason" class="sr-only">
    {{ uploadDisabledReason() }}
  </p>

  <!--
    Live region — always in the DOM so screen readers register it early.
    Announces status transitions (uploading → success / error) without
    requiring focus to move.
  -->
  <div aria-live="polite" aria-atomic="true" class="sr-only">
    @if (isLoading()) {
      Uploading {{ fileName() }}, please wait.
    } @else if (fileName() && !hasError()) {
      {{ fileName() }} uploaded successfully. A confirmation comment has been
      posted.
    } @else if (fileName() && hasError()) {
      Upload failed for {{ fileName() }}. {{ fileUploadError() }}
    }
  </div>

  <!-- File card -->
  @if (fileName()) {
    <div
      role="region"
      [attr.aria-label]="'Upload status for ' + fileName()"
      class="rounded-lg border px-4 py-3"
      [class.border-gray-200]="!hasError()"
      [class.bg-white]="!hasError()"
      [class.border-red-600]="hasError()"
      [class.bg-red-50]="hasError()"
      [class.opacity-75]="isLoading()"
    >
      <div class="flex items-start justify-between gap-4">
        <div class="flex items-start gap-3">
          <mat-icon
            class="mt-0.5 shrink-0"
            [class.text-gray-400]="!hasError()"
            [class.text-red-600]="hasError()"
            aria-hidden="true"
            >description</mat-icon
          >

          <div class="min-w-0">
            <!-- Full filename always readable by screen readers; truncation is visual only -->
            <div
              class="font-lato truncate text-sm font-medium"
              [class.text-brand-text]="!hasError()"
              [class.text-red-600]="hasError()"
              [title]="fileName()"
            >
              {{ fileName() }}
            </div>

            <!-- Status line (visual; announcements handled by live region above) -->
            <div
              class="mt-1 flex items-center gap-1.5 text-xs font-semibold"
              aria-hidden="true"
            >
              @if (isLoading()) {
                <mat-spinner diameter="12" aria-label="Uploading"></mat-spinner>
                <span class="text-gray-400">Uploading...</span>
              } @else if (hasError()) {
                <mat-icon
                  class="!flex !h-3.5 !w-3.5 !items-center !justify-center !text-sm text-red-600"
                  aria-hidden="true"
                  >error</mat-icon
                >
                <span class="text-red-600">Upload failed</span>
              } @else {
                <mat-icon
                  class="!flex !h-3.5 !w-3.5 !items-center !justify-center !text-sm text-green-700"
                  aria-hidden="true"
                  >check_circle</mat-icon
                >
                <span class="text-green-700">Uploaded · comment posted</span>
              }
            </div>
          </div>
        </div>

        <!-- Error actions -->
        @if (!isLoading() && hasError()) {
          <div class="flex shrink-0 items-center gap-2">
            <button
              type="button"
              class="font-lato h-7 rounded-lg border border-gray-300 bg-white px-3 text-xs text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
              (click)="fileUploadClick()"
            >
              Try another file
            </button>
            <button
              type="button"
              class="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
              (click)="clearSelectedFile()"
              [attr.aria-label]="'Dismiss error for ' + fileName()"
            >
              <mat-icon
                class="!flex !h-4 !w-4 !items-center !justify-center !text-base"
                aria-hidden="true"
                >close</mat-icon
              >
            </button>
          </div>
        }
      </div>

      <!-- Error detail — role="alert" announces immediately on appearance -->
      @if (hasError()) {
        <div role="alert" class="mt-2.5 text-xs text-red-600">
          {{ fileUploadError() }}
          @if (fileUploadError()?.toLowerCase()?.includes('character')) {
            <span class="ml-1 font-mono">e.g. 2025-01-01_Invoice.pdf</span>
          }
        </div>
      }
    </div>
  }
</div>
